/**
 * Contrato da campanha #mostraseuaprova com o backend (`/mostra-seu-aprova`).
 *
 * Quem decide o que pode ser exibido é o servidor: ele conhece a janela, o
 * status de inscrição (que vem de uma base externa) e os prazos. O front só
 * consome este estado e revalida as viradas de horário localmente, porque uma
 * aba pode ficar aberta por dias.
 */

export type EnrollmentStatus =
  | 'NAO_INSCRITO'
  | 'INSCRITO_SEM_VIDEO'
  | 'VIDEO_EM_VALIDACAO'
  | 'VIDEO_VALIDADO';

export interface iCampaignWindow {
  /** ISO com offset de Brasília. */
  startDate: string;
  endDate: string;
  isActive: boolean;
}

export interface iTriggerView {
  /** 1, 2 ou 3. */
  slot: number;
  viewedAt: string;
}

export interface iMostraSeuAprovaState {
  campaign: iCampaignWindow;
  /**
   * Destino do botão dos modais de gatilho, já resolvido pelo servidor conforme
   * a situação do aluno: a inscrição para quem não se inscreveu, o envio do
   * vídeo para quem já se inscreveu. `null` quando não há destino configurado.
   */
  ctaUrl: string | null;
  /** `null` quando a base de inscrição não respondeu — nada deve ser exibido. */
  enrollmentStatus: EnrollmentStatus | null;
  presentation: {
    eligible: boolean;
    seenAt: string | null;
  };
  triggers: {
    seen: iTriggerView[];
    /** Próximo slot a consumir, ou `null` quando os três já foram exibidos. */
    nextSlot: number | null;
    /** Quando o `nextSlot` libera. `null` = não há marco para contar ainda. */
    unlockAt: string | null;
    eligibleNow: boolean;
  };
}

export type ViewKind = 'presentation' | 'trigger';

export interface iRegisterViewPayload {
  kind: ViewKind;
  /** Variação exibida (ex.: `flashcards`). Só alimenta telemetria. */
  triggerKey?: string;
}

export interface iRegisterViewResponse {
  /** `false` quando o servidor recusou — o estado devolvido explica por quê. */
  registered: boolean;
  /**
   * Qual modal exibir. O cliente pede um gatilho, mas quem decide é o servidor:
   * quem ainda não viu a apresentação recebe o vídeo neste gatilho, e a variação
   * por produto fica para o próximo.
   */
  kind: ViewKind | null;
  slot: number | null;
  state: iMostraSeuAprovaState;
}

const toTime = (date?: string | null): number | null => {
  if (!date) return null;

  const time = new Date(date).getTime();

  return Number.isNaN(time) ? null : time;
};

/**
 * Revalida a janela com o relógio do cliente.
 *
 * O `isActive` vem calculado do servidor, mas envelhece: a resposta fica em
 * cache no react-query e nada a refaz numa aba aberta. Sem esta checagem, um
 * modal continuaria elegível depois das 23:59 do dia 16.
 */
const isCampaignActiveAt = (
  campaign: iCampaignWindow | undefined,
  nowMs: number
): boolean => {
  if (!campaign) return false;

  const start = toTime(campaign.startDate);
  const end = toTime(campaign.endDate);

  if (start !== null && nowMs < start) return false;
  if (end !== null && nowMs > end) return false;

  return true;
};

/** O prazo do próximo slot já venceu no relógio do cliente? */
const isTriggerUnlockedAt = (
  state: iMostraSeuAprovaState | undefined,
  nowMs: number
): boolean => {
  const unlockAt = toTime(state?.triggers.unlockAt);

  return unlockAt !== null && nowMs >= unlockAt;
};

/**
 * Próximos instantes em que a elegibilidade muda sozinha: fim da campanha e
 * liberação do próximo slot. Alimenta o timer que reavalia a tela.
 */
const getCampaignBoundaries = (
  state: iMostraSeuAprovaState | undefined
): number[] => {
  if (!state) return [];

  return [
    toTime(state.campaign.startDate),
    toTime(state.campaign.endDate),
    toTime(state.triggers.unlockAt),
  ].filter((time): time is number => time !== null);
};

export {
  getCampaignBoundaries,
  isCampaignActiveAt,
  isTriggerUnlockedAt,
  toTime,
};
